import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../authContext';
import { LoadingSpinner } from '../../general/LoadingSpinner';
import { logger } from '../../../utils/logger';
import { hasProductAccess } from '../../../utils/productAccess';
import { isPublishedReportPath } from '../../../config/publishedReports';
import { isLocal } from '../../../config/env';

export const ProtectedRoute = () => {
  const { isAuthenticated, isLoading, currentUser } = useAuth();
  const location = useLocation();
  
  logger.log('Protected route check:', { 
    path: location.pathname,
    isAuthenticated, 
    isLoading,
    hasToken: !!localStorage.getItem('authToken')
  });
  
  logger.log('Protected route current user:', currentUser);

  // Show loading indicator while checking auth status
  if (isLoading) {
    return <LoadingSpinner message="Loading..." />;
  }
  
  // Redirect to login if not authenticated, or user is logged out
  if (!isAuthenticated || !currentUser) {
    if (isPublishedReportPath(location.pathname)) {
      return <Outlet />;
    }
    logger.log('User not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const path = location.pathname || '';

  if (path.startsWith('/sage')) {
    if (!currentUser.has_sage_access) {
      logger.log('User lacks Sage access, redirecting to home', { path });
      return <Navigate to="/home" state={{ from: location }} replace />;
    }
    logger.log('Sage path allowed', { path });
    return <Outlet />;
  }

  if (isLocal && path.startsWith('/utility')) {
    const amptrans = String(currentUser?.email || "").trim().toLowerCase().endsWith("@amptrans.net");
    if (!amptrans) {
      logger.log('GridPlan is limited to AmpTrans accounts on local dev', { path });
      return <Navigate to="/home" state={{ from: location }} replace />;
    }
    return <Outlet />;
  }

  if (isLocal && !path.startsWith('/internal')) {
    logger.log('Local dev mode: bypassing module access checks');
    return <Outlet />;
  }

  // Optional: Check subscription status (currently commented out)
  // if (currentUser.subscription_status === null || currentUser.subscription_status === 'cancelled' || currentUser.subscription_status === 'inactive') {
  //   logger.log('User has no subscription, redirecting to subscription page');
  //   return <Navigate to="/subscription" state={{ from: location }} replace />;
  // }
  
  if (isPublishedReportPath(path)) {
    logger.log('Published report path allowed without product entitlement', { path });
    return <Outlet />;
  }

  const protectedProductPaths = [
    { prefix: '/fleet', productId: 'efleetplan' },
    { prefix: '/charger', productId: 'chargerplan' },
    { prefix: '/utility', productId: 'gridplan' },
    { prefix: '/data', productId: 'datahub' },
    { prefix: '/internal', productId: 'internalops' },
    { prefix: '/regulator', productId: 'policyplan' },
  ];

  const matchedProtectedPath = protectedProductPaths.find(({ prefix }) => path.startsWith(prefix));
  if (matchedProtectedPath && !currentUser.email_verified) {
    logger.log('Email not verified, redirecting to home before module access', { path });
    return <Navigate to="/home" state={{ from: location, requiresVerification: true }} replace />;
  }

  if (matchedProtectedPath && !hasProductAccess(currentUser, matchedProtectedPath.productId)) {
    logger.log('User lacks product access, redirecting to home', {
      path,
      productId: matchedProtectedPath.productId,
    });
    return <Navigate to="/home" state={{ from: location }} replace />;
  }

  // Render child routes if authenticated
  logger.log('User is authenticated, rendering protected content');
  return <Outlet />;
};